/**
 * Card Variant Picker
 * Handles variant selection on product cards in collection pages
 * Includes virtual bundle support
 */

class CardVariantPicker {
  constructor() {
    this.purpleDotFallbackDelay = 1500;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.attachEventListeners());
    } else {
      this.attachEventListeners();
    }
  }

  attachEventListeners() {
    // Handle radio button changes (color swatches)
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('card-variant-radio')) {
        e.preventDefault();
        e.stopPropagation();
        this.handleVariantChange(e.target);
      }
    });

    // Handle dropdown changes (size, etc.)
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('card-variant-select')) {
        e.preventDefault();
        e.stopPropagation();
        this.handleVariantChange(e.target);
      }
    });

    // Handle pack/pot/tray quantity dropdown changes on collection cards
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('eg-card-qty-select')) {
        this.handleQtyChange(e.target);
      }
    });

    // Intercept form submissions to ensure variant ID is updated
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.dataset.type === 'add-to-cart-form') {
        const card = form.closest('.recommendations-slide, .card-wrapper');
        if (!card) return;

        if (card.dataset.invalidVariantSelection === 'true'
          || card.querySelector('[data-invalid-variant-selection="true"]')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }

        if (
          card.dataset.purpleDotPreorderPending === 'true'
          && card.dataset.purpleDotReady !== 'true'
        ) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }

        const variantInput = form.querySelector('.product-variant-id');
        if (!variantInput) return;

        const forcedMappedVariantId = variantInput.dataset.forceMappedVariantId;
        if (forcedMappedVariantId) {
          variantInput.value = forcedMappedVariantId;
          variantInput.setAttribute('value', forcedMappedVariantId);
          return;
        }

        const selectedVariantId = card.dataset.selectedVariantId;
        if (selectedVariantId && variantInput.value !== selectedVariantId) {
          variantInput.value = selectedVariantId;
          variantInput.setAttribute('value', selectedVariantId);
        }
      }
    }, true); // Use capture phase

    if (!this.purpleDotFallbackClickHandlerBound) {
      this.purpleDotFallbackClickHandlerBound = true;
      document.addEventListener('click', (event) => {
        const button = event.target.closest('.quick-add__submit[data-preorder-pdp-fallback]');
        if (!button) return;

        const card = button.closest('.recommendations-slide, .card-wrapper');
        const productLink = card?.querySelector('[data-card-product-link]');
        if (!productLink?.href) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        productLink.click();
      }, true);
    }

    if (!this.cartDrawerPreorderHandlerBound) {
      this.cartDrawerPreorderHandlerBound = true;
      document.addEventListener('cart-drawer:updated', () => {
        window.requestAnimationFrame(() => {
          const drawer = document.querySelector('cart-drawer');
          if (!drawer) return;
          this.setupCustomPreorderCards(drawer);
          this.setupPurpleDotManagedCards(drawer);
        });
      });
    }

    // Initialize all cards with their selected variants
    this.initializeAllCards();
    this.updateStaticMemberPriceUpsells();
    this.setupCustomPreorderCards();
    this.setupPurpleDotManagedCards();

    document.addEventListener('ig:pricesUpdated', () => {
      this.refreshAllQtyDropdownLabelsFromDisplayedPrices();
    });

    setTimeout(() => this.refreshAllQtyDropdownLabelsFromDisplayedPrices(), 500);
    setTimeout(() => this.refreshAllQtyDropdownLabelsFromDisplayedPrices(), 1500);
  }

  /**
   * Check if a card is a virtual bundle and get its config
   */
  getVirtualBundleConfig(card, productId) {
    const cardEl = card.querySelector('.card[data-is-virtual-bundle="true"]');
    if (!cardEl) return null;
    
    const configScript = card.querySelector(`script[id*="virtual-bundle-"][id*="${productId}"]`);
    if (!configScript) return null;
    
    try {
      const config = JSON.parse(configScript.textContent);
      return config?.isVirtualBundle ? config : null;
    } catch (e) {
      console.error('Error parsing virtual bundle config:', e);
      return null;
    }
  }

  getVariantPreorderState(card, variant) {
    let states = null;
    const variantElement = card?.querySelector('[data-product-id]');
    const productId = variantElement?.dataset?.productId || '';
    const virtualBundleConfig = this.getVirtualBundleConfig(card, productId);
    if (virtualBundleConfig?.singleProductPreorderStates && variant?.id) {
      states = virtualBundleConfig.singleProductPreorderStates;
    }
    const statesScript = card.querySelector('script[id*="preorder-variants-"]');
    if (!statesScript || !variant?.id) {
      return states?.[String(variant?.id)] || { active: card?.dataset?.customPreorderCard === 'true', timeline: '' };
    }
    try {
      const productStates = JSON.parse(statesScript.textContent);
      const preorderStates = states || productStates;
      return preorderStates[String(variant.id)] || { active: false, timeline: '' };
    } catch (_) {
      return { active: false, timeline: '' };
    }
  }

  getSelectedVariant(card) {
    const variantsScript = card.querySelector('script[id*="variants-"]');
    if (!variantsScript) return null;
    try {
      const variantElement = card.querySelector('[data-product-id]');
      const productId = variantElement?.dataset?.productId || '';
      const virtualBundleConfig = this.getVirtualBundleConfig(card, productId);
      const variants = virtualBundleConfig?.singleProductVariants || JSON.parse(variantsScript.textContent);
      const selectedId = card.dataset.selectedVariantId || card.querySelector('.product-variant-id')?.value;
      return variants.find((variant) => String(variant.id) === String(selectedId)) || null;
    } catch (_) {
      return null;
    }
  }

  getAvailableButtonLabel(card, variant = this.getSelectedVariant(card)) {
    const preorderState = this.getVariantPreorderState(card, variant);
    // Purple Dot inventory candidates are not confirmed preorders until its
    // form has finished resolving. Keep the normal CTA label while pending;
    // Purple Dot will replace it after it marks the form preorder-ready.
    return preorderState.active
      ? window.theme?.strings?.preorder || 'Pre-Order'
      : window.theme?.strings?.addToCart || 'Add to cart';
  }

  isPurpleDotPreorderVariant(card, variant) {
    const isInventoryPreorder = variant?.available
      && variant.inventory_management === 'shopify'
      && Number(variant.inventory_quantity) <= 0
      && variant.inventory_policy === 'continue';
    if (isInventoryPreorder) return true;

    const statesScript = card?.querySelector('[data-purple-dot-preorder-variants]');
    if (!statesScript || !variant?.id) return false;

    try {
      const states = JSON.parse(statesScript.textContent);
      return states[String(variant.id)] === true;
    } catch (_) {
      return false;
    }
  }

  syncPurpleDotPendingState(card, variant = this.getSelectedVariant(card)) {
    if (card?.dataset.invalidVariantSelection === 'true') return;
    if (card?.dataset?.purpleDotManagedCard !== 'true') return;

    const form = card.querySelector('form[data-type="add-to-cart-form"]');
    const isPurpleDotManaged = form?.classList.contains('x-pd-atc-form');
    const isPurpleDotPreorderReady = form?.classList.contains('x-pd-atc-form--preorder')
      || !!form?.querySelector('[name="properties[__releaseId]"], [name="properties[Purple Dot Pre-order]"]');
    const isPurpleDotInStockReady = form?.classList.contains('x-pd-atc-form--instock');
    const isPendingPreorder = this.isPurpleDotPreorderVariant(card, variant);

    const isReady = (
      isPendingPreorder
        ? isPurpleDotPreorderReady
        : !isPurpleDotManaged || isPurpleDotInStockReady
    );
    card.dataset.purpleDotPreorderPending = String(isPendingPreorder);
    card.dataset.purpleDotReady = String(isReady);

    const button = card.querySelector('.quick-add__submit');
    if (button && isPendingPreorder && !isReady) {
      if (button.hasAttribute('data-preorder-pdp-fallback')) return;

      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('data-purple-dot-pending-button', '');
      this.schedulePurpleDotPdpFallback(card);
    } else if (button) {
      this.clearPurpleDotPdpFallback(button);
      button.removeAttribute('aria-disabled');
      button.removeAttribute('data-purple-dot-pending-button');
    }
  }

  schedulePurpleDotPdpFallback(card) {
    if (!card || card.dataset.purpleDotFallbackScheduled === 'true') return;
    card.dataset.purpleDotFallbackScheduled = 'true';

    window.setTimeout(() => {
      delete card.dataset.purpleDotFallbackScheduled;
      if (
        card.dataset.purpleDotPreorderPending === 'true'
        && card.dataset.purpleDotReady !== 'true'
      ) {
        this.enablePurpleDotPdpFallback(card);
      }
    }, this.purpleDotFallbackDelay);
  }

  enablePurpleDotPdpFallback(card) {
    const button = card?.querySelector('.quick-add__submit');
    if (!button) return;

    button.type = 'button';
    button.disabled = false;
    button.removeAttribute('name');
    button.removeAttribute('aria-disabled');
    button.removeAttribute('data-purple-dot-pending-button');
    button.setAttribute('data-preorder-pdp-fallback', '');
  }

  clearPurpleDotPdpFallback(button) {
    if (!button?.hasAttribute('data-preorder-pdp-fallback')) return;

    button.removeAttribute('data-preorder-pdp-fallback');
    button.type = 'submit';
    button.name = 'add';
  }

  setupPurpleDotManagedCards(scope = document) {
    scope.querySelectorAll('[data-purple-dot-managed-card="true"]').forEach((card) => {
      this.syncPurpleDotPendingState(card);
      if (card.dataset.purpleDotObserverReady === 'true') return;
      card.dataset.purpleDotObserverReady = 'true';

      const observer = new MutationObserver(() => this.syncPurpleDotPendingState(card));
      observer.observe(card, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    });
  }

  updateProductLinks(card, variantId) {
    if (!variantId) return;

    card.querySelectorAll('[data-card-product-link]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      url.searchParams.set('variant', variantId);
      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    });
  }

  enforceCustomPreorderCard(card) {
    const form = card.querySelector('form[data-type="add-to-cart-form"]');
    const isPurpleDotManaged = form?.classList.contains('x-pd-atc-form')
      || !!form?.querySelector('[name="properties[__releaseId]"], [name="properties[Purple Dot Pre-order]"]');
    if (isPurpleDotManaged) return;

    card.querySelectorAll('#pd-waitlist-info, .pd-waitlist-info, purple-dot-learn-more').forEach((element) => element.remove());
    card.querySelectorAll(
      '[name="properties[__pdDebug]"], [name="properties[__releaseId]"], [name="properties[Purple Dot Pre-order]"]'
    ).forEach((input) => input.remove());

    if (form) form.classList.remove('x-pd-atc-form--preorder');

    const submitButton = card.querySelector('.quick-add__submit');
    const buttonText = submitButton?.querySelector('span:first-child');
    if (!submitButton || !buttonText || submitButton.disabled || submitButton.classList.contains('loading')) return;

    const currentText = buttonText.textContent.trim();
    const preorderLabel = this.getAvailableButtonLabel(card);
    if (!/added|error/i.test(currentText) && currentText !== preorderLabel) {
      buttonText.textContent = preorderLabel;
    }
  }

  setupCustomPreorderCards(scope = document) {
    scope.querySelectorAll('[data-custom-preorder-card="true"]').forEach((card) => {
      this.enforceCustomPreorderCard(card);
      if (card.dataset.customPreorderReady === 'true') return;
      card.dataset.customPreorderReady = 'true';

      const observer = new MutationObserver(() => this.enforceCustomPreorderCard(card));
      observer.observe(card, { childList: true, subtree: true });
    });
  }

  initializeAllCards(scope = document) {
    const cards = scope.querySelectorAll('.card-wrapper');
    
    cards.forEach(card => {
      // Get the product ID from any variant element
      const variantElement = card.querySelector('[data-product-id]');
      if (!variantElement) return;
      
      const productId = variantElement.dataset.productId;
      
      // Check if this is a virtual bundle
      const virtualBundleConfig = this.getVirtualBundleConfig(card, productId);
      
      // Get all selected options for this card
      const selectedOptions = this.getSelectedOptions(card, productId);
      
      if (virtualBundleConfig) {
        // Virtual bundle: use the linked single-product variants for availability.
        const singleVariants = virtualBundleConfig.singleProductVariants;
        const matchingSingleVariant = this.findMatchingVariant(singleVariants, selectedOptions);
        
        if (matchingSingleVariant) {
          this.updateCardForVirtualBundle(card, matchingSingleVariant, virtualBundleConfig, productId, selectedOptions);
        }
      } else {
        // Regular product: use this product’s variants.
        const variantsScript = card.querySelector(`script[id*="variants-"][id*="${productId}"]`);
        if (!variantsScript) return;

        let variants;
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (e) {
          console.error('Error parsing variants JSON:', e);
          return;
        }

        const matchingVariant = this.findMatchingVariant(variants, selectedOptions);
        
        if (matchingVariant) {
          this.updateCard(card, matchingVariant, productId);
        }
      }
    });
  }

  handleVariantChange(element) {
    const productId = element.dataset.productId;
    const card = element.closest('.card-wrapper');
    
    if (!card) return;

    // Update selected value display for radio buttons
    if (element.classList.contains('card-variant-radio')) {
      const optionPosition = element.dataset.optionPosition;
      const selectedSpan = card.querySelector(`[id*="selected-"][id*="-option${optionPosition}"]`);
      if (selectedSpan) {
        selectedSpan.textContent = element.value;
      }
    }

    // Get all selected options
    const selectedOptions = this.getSelectedOptions(card, productId);
    
    // Check if this is a virtual bundle
    const virtualBundleConfig = this.getVirtualBundleConfig(card, productId);
    
    if (virtualBundleConfig) {
      // Virtual bundle: use the linked single-product variants.
      const singleVariants = virtualBundleConfig.singleProductVariants;
      const matchingSingleVariant = this.findMatchingVariant(singleVariants, selectedOptions);
      
      if (matchingSingleVariant) {
        this.updateCardForVirtualBundle(card, matchingSingleVariant, virtualBundleConfig, productId, selectedOptions);
      } else {
        this.setSelectionValidity(card, false);
      }
    } else {
      // Regular product: use this product’s variants.
      const variantsScript = card.querySelector(`script[id*="variants-"][id*="${productId}"]`);
      if (!variantsScript) return;

      let variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('Error parsing variants JSON:', e);
        return;
      }

      const matchingVariant = this.findMatchingVariant(variants, selectedOptions);
      
      if (matchingVariant) {
        this.updateCard(card, matchingVariant, productId);
      } else {
        this.setSelectionValidity(card, false);
      }
    }
  }

  setSelectionValidity(card, valid) {
    const wasInvalid = card.dataset.invalidVariantSelection === 'true';
    card.dataset.invalidVariantSelection = String(!valid);
    card.querySelectorAll('[id^="price-"], [id^="discount-badge-"], [data-card-member-savings]')
      .forEach((element) => element.classList.toggle('hidden', !valid));
    if (valid) {
      if (wasInvalid) {
        const input = card.querySelector('.product-variant-id');
        if (input) input.disabled = false;
        const button = card.querySelector('.quick-add__submit');
        if (button) button.disabled = false;
      }
      return;
    }

    delete card.dataset.selectedVariantId;
    const slide = card.closest('.recommendations-slide');
    if (slide) delete slide.dataset.selectedVariantId;
    const input = card.querySelector('.product-variant-id');
    if (input) {
      input.value = '';
      input.setAttribute('value', '');
      input.disabled = true;
    }
    const button = card.querySelector('.quick-add__submit');
    if (button) {
      this.clearPurpleDotPdpFallback(button);
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      const label = button.querySelector('span:first-child');
      if (label) label.textContent = window.theme?.strings?.unavailable || 'Unavailable';
    }
  }

  /**
   * Update a card that represents a virtual bundle.
   * - Availability/cart behavior comes from the linked single-product variant.
   * - Price/image display comes from the bundle product variant.
   */
  updateCardForVirtualBundle(card, singleVariant, config, productId, selectedOptions) {
    this.setSelectionValidity(card, true);
    // Store the linked single-product variant ID for cart submission.
    const slideContainer = card.closest('.recommendations-slide');
    if (slideContainer) {
      slideContainer.dataset.selectedVariantId = singleVariant.id;
    }
    card.dataset.selectedVariantId = singleVariant.id;

    // Keep the hidden variant input in sync with the linked single-product variant ID.
    let variantInput = card.querySelector('.product-variant-id');
    if (!variantInput && slideContainer) {
      variantInput = slideContainer.querySelector('.product-variant-id');
    }
    
    if (variantInput) {
      variantInput.value = singleVariant.id;
      variantInput.setAttribute('value', singleVariant.id);
      // Keep the virtual bundle data attributes
      variantInput.dataset.isVirtualBundle = 'true';
      variantInput.dataset.bundleQuantity = config.bundleQuantity;
    }

    // Find the matching bundle variant for price/image display.
    const bundleVariantsScript = card.querySelector(`script[id*="variants-"][id*="${productId}"]`);
    let bundleVariant = null;
    if (bundleVariantsScript) {
      try {
        const bundleVariants = JSON.parse(bundleVariantsScript.textContent);
        bundleVariant = this.findMatchingVariant(bundleVariants, selectedOptions);
      } catch (e) {
        console.error('Error parsing bundle variants:', e);
      }
    }

    // Update price/image from the bundle variant (Intelligems pricing).
    if (bundleVariant) {
      this.syncPurpleDotPendingState(card, singleVariant);
      this.updateProductLinks(card, bundleVariant.id);
      this.updatePrice(card, bundleVariant, productId);
      this.updateQtyDropdownLabels(card, bundleVariant);
      this.updateDiscountBadge(card, bundleVariant, productId);
      this.updateImage(card, bundleVariant, productId);
    }

    // Update the button state based on linked single-product availability.
    this.updateButtonForVirtualBundle(card, singleVariant);
    this.syncPurpleDotPendingState(card, singleVariant);
    
    // Update option availability based on linked single-product variants.
    this.updateAvailableOptionsForVirtualBundle(card, config.singleProductVariants);
  }

  updateButtonForVirtualBundle(card, singleVariant) {
    const submitButton = card.querySelector('.quick-add__submit');
    if (!submitButton) return;
    if (submitButton.matches('.button--early-access, [data-early-access-end]')) return;

    const buttonText = submitButton.querySelector('span:first-child');
    if (!buttonText) return;
    const preorderTimeline = card.querySelector('[data-custom-preorder-timeline], .card__custom-preorder-timeline');
    const preorderState = this.getVariantPreorderState(card, singleVariant);
    if (preorderTimeline) {
      preorderTimeline.hidden = !preorderState.active;
      if (preorderState.timeline) preorderTimeline.innerHTML = preorderState.timeline;
    }
    const variantInput = card.querySelector('.product-variant-id') || card.closest('.recommendations-slide')?.querySelector('.product-variant-id');

    if (singleVariant.available) {
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-disabled');
      if (variantInput) variantInput.disabled = false;
      buttonText.textContent = this.getAvailableButtonLabel(card);
    } else {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      if (variantInput) variantInput.disabled = true;
      buttonText.textContent = window.theme?.strings?.soldOut || 'Sold out';
    }
  }

  updateAvailableOptionsForVirtualBundle(card, singleVariants) {
    // Update radio buttons based on single product availability
    const radioButtons = card.querySelectorAll('.card-variant-radio');
    radioButtons.forEach(radio => {
      const optionPosition = parseInt(radio.dataset.optionPosition);
      const optionValue = radio.value;
      
      // Check if this option is available in any single product variant
      const isAvailable = singleVariants.some(variant => {
        return variant[`option${optionPosition}`] === optionValue && variant.available;
      });
      
      radio.disabled = !isAvailable;
      
      const swatchLabel = card.querySelector(`label[for="${radio.id}"]`) || radio.closest('label');
      if (swatchLabel) {
        const pill = swatchLabel.querySelector('.color-pill');
        const targetEl = pill || swatchLabel;
        targetEl.classList.toggle('disabled', !isAvailable);
      }
    });
  }

  getSelectedOptions(card, productId) {
    const selectedOptions = [];

    // Get values from radio buttons (color swatches)
    const radioButtons = card.querySelectorAll('.card-variant-radio:checked');
    radioButtons.forEach(radio => {
      const position = parseInt(radio.dataset.optionPosition) - 1;
      selectedOptions[position] = radio.value;
    });

    // Get values from dropdowns
    const selects = card.querySelectorAll('.card-variant-select');
    selects.forEach(select => {
      const position = parseInt(select.dataset.optionPosition) - 1;
      selectedOptions[position] = select.value;
    });

    // Collection cards can initialize before their controls are fully upgraded.
    // Preserve the in-stock option selected by Liquid instead of falling back to variant 1.
    if (!selectedOptions[0] && card.dataset.initialOption1) {
      selectedOptions[0] = card.dataset.initialOption1;
    }

    return selectedOptions;
  }

  findMatchingVariant(variants, selectedOptions) {
    return variants.find(variant => {
      return selectedOptions.every((value, index) => {
        const variantOption = variant[`option${index + 1}`];
        return variantOption === value;
      });
    });
  }

  updateCard(card, variant, productId) {
    this.setSelectionValidity(card, true);
    // Store the selected variant ID on the card element for form submission
    const slideContainer = card.closest('.recommendations-slide');
    if (slideContainer) {
      slideContainer.dataset.selectedVariantId = variant.id;
    }
    card.dataset.selectedVariantId = variant.id;
    this.syncPurpleDotPendingState(card, variant);
    this.updateProductLinks(card, variant.id);

    // Update hidden variant input
    let variantInput = card.querySelector('.product-variant-id');
    
    if (!variantInput) {
      if (slideContainer) {
        variantInput = slideContainer.querySelector('.product-variant-id');
      }
    }

    let forcedMappedAvailable = null;
    
    if (variantInput) {
      const forcedMappedVariantId = variantInput.dataset.forceMappedVariantId;
      forcedMappedAvailable = variantInput.dataset.forceMappedAvailable;
      if (forcedMappedVariantId) {
        variantInput.value = forcedMappedVariantId;
        variantInput.setAttribute('value', forcedMappedVariantId);
      } else {
        variantInput.value = variant.id;
        // Force a DOM update
        variantInput.setAttribute('value', variant.id);
      }
    } else {
      console.error('Could not find variant input for product', productId);
    }

    const mappedHandle = String(card?.querySelector('product-form')?.dataset?.productHandle || '').trim();
    const shouldKeepMappedDisplayPrice = mappedHandle === '3-cube-pot' && !!variantInput?.dataset?.forceMappedVariantId;

    if (!shouldKeepMappedDisplayPrice) {
      this.updatePrice(card, variant, productId);
      this.updateQtyDropdownLabels(card, variant);
      this.updateDiscountBadge(card, variant, productId);
    }

    // Update product image (needs to update srcset too, not just src)
    this.updateImage(card, variant, productId);

    // For cards forced to mapped bundle variants, keep button state tied to mapped availability.
    if (variantInput && variantInput.dataset.forceMappedVariantId) {
      this.updateButton(card, { available: forcedMappedAvailable === 'true' });
    } else {
      this.updateButton(card, variant);
    }
    this.syncPurpleDotPendingState(card, variant);
  }

  handleQtyChange(selectEl) {
    const card = selectEl.closest('.recommendations-slide, .card-wrapper');
    if (!card) return;

    const mappedHandle = String(card?.querySelector('product-form')?.dataset?.productHandle || '').trim();
    const variantInput = card.querySelector('.product-variant-id') || card.closest('.recommendations-slide')?.querySelector('.product-variant-id');
    const shouldKeepMappedDisplayPrice = mappedHandle === '3-cube-pot' && !!variantInput?.dataset?.forceMappedVariantId;
    if (shouldKeepMappedDisplayPrice) return;

    const variantEl = card.querySelector('[data-product-id]');
    const productId = variantEl?.dataset?.productId;
    if (!productId) return;

    const variant = this.getCurrentVariantForCard(card, productId);
    if (!variant) return;

    this.updatePrice(card, variant, productId);
    this.updateQtyDropdownLabels(card, variant);
    this.updateDiscountBadge(card, variant, productId);
  }

  getCurrentVariantForCard(card, productId) {
    const selectedVariantId =
      card.dataset.selectedVariantId ||
      card.querySelector('.product-variant-id')?.value ||
      card.closest('.recommendations-slide')?.dataset?.selectedVariantId ||
      card.closest('.recommendations-slide')?.querySelector('.product-variant-id')?.value;
    if (!selectedVariantId) return null;

    const variantsScript = card.querySelector(`script[id*="variants-"][id*="${productId}"]`);
    if (!variantsScript) return null;

    try {
      const variants = JSON.parse(variantsScript.textContent);
      return variants.find((variant) => String(variant.id) === String(selectedVariantId)) || null;
    } catch (e) {
      console.error('Error parsing variants JSON for quantity change:', e);
      return null;
    }
  }

  getSelectedCardQty(card) {
    const qtySelect = card.querySelector('.eg-card-qty-select');
    const qty = parseInt(qtySelect?.value, 10);
    if (!qty || qty < 1) return 1;
    return qty;
  }

  getMemberPriceConfig(card) {
    const configElement = card.querySelector('[data-card-member-price-config]');
    if (!configElement) return null;

    try {
      return JSON.parse(configElement.textContent);
    } catch (_) {
      return null;
    }
  }

  getMemberOverridePrice(card, variant, quantity) {
    const config = this.getMemberPriceConfig(card);
    if (!config || !variant) return null;

    const variantConfig = config.variants?.find((item) => String(item.id) === String(variant.id));
    let rawOverrides = variantConfig?.override == null ? config.productOverride : variantConfig.override;
    if (typeof rawOverrides === 'string') {
      try {
        rawOverrides = JSON.parse(rawOverrides);
      } catch (_) {
        return null;
      }
    }
    const overrides = Array.isArray(rawOverrides) ? rawOverrides : rawOverrides ? [rawOverrides] : [];
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    const override = overrides
      .filter((item) => {
        const minQty = Number(item?.minQty ?? 1);
        const value = Number(item?.value);
        const type = String(item?.type || '').toLowerCase();
        const startsAt = item?.startsAt;
        const endsAt = item?.endsAt;
        return ['amount', 'percentage'].includes(type)
          && Number.isFinite(value) && value > 0
          && Number.isFinite(minQty) && minQty >= 1 && quantity >= minQty
          && (!startsAt || startsAt <= today) && (!endsAt || endsAt >= today);
      })
      .sort((a, b) => Number(b.minQty ?? 1) - Number(a.minQty ?? 1))[0];

    if (!override) return null;

    const value = Number(override.value);
    if (String(override.type).toLowerCase() === 'percentage') {
      return Math.round(variant.price * (1 - Math.min(value, 100) / 100));
    }

    const discountCents = value * 100;
    return Math.round(Math.max(0, variant.price - (override.perItem === true ? discountCents : discountCents / quantity)));
  }

  getVirtualBundleMemberPricingContext(card, bundleVariant) {
    const cardElement = card.querySelector('.card[data-is-virtual-bundle="true"]');
    const productId = cardElement?.dataset?.productId;
    if (!productId) return null;

    const config = this.getVirtualBundleConfig(card, productId);
    if (!config?.singleProductVariants?.length) return null;

    const selectedSingleVariantId =
      card.dataset.selectedVariantId || card.querySelector('.product-variant-id')?.value;
    let singleVariant = config.singleProductVariants.find(
      (item) => String(item.id) === String(selectedSingleVariantId)
    );

    if (!singleVariant) {
      const selectedOptions = this.getSelectedOptions(card, productId);
      singleVariant = this.findMatchingVariant(config.singleProductVariants, selectedOptions);
    }

    const bundleQuantity = Math.max(1, parseInt(config.bundleQuantity, 10) || 1);
    if (!singleVariant || !bundleVariant) return null;

    return { bundleQuantity, singleVariant };
  }

  updateStaticMemberPriceUpsells(scope = document) {
    scope.querySelectorAll('[data-static-member-price-upsell]').forEach((upsell) => {
      const card = upsell.closest('.card-wrapper');
      const variantId = upsell.dataset.variantId;
      const price = Number(upsell.dataset.priceCents);
      if (!card || !variantId || !price) return;

      const overridePrice = this.getMemberOverridePrice(card, { id: variantId, price }, 1);
      if (overridePrice === null) return;

      const amount = upsell.querySelector('.member-savings-amount');
      if (amount) amount.textContent = this.formatMoney(overridePrice);
    });
  }

  getBulkDiscountPct(card, qty) {
    const configuredTiers = this.getMemberPriceConfig(card)?.bulkPricingTiers;
    let tiers = configuredTiers;
    if (typeof tiers === 'string') {
      try {
        tiers = JSON.parse(tiers);
      } catch (_) {
        tiers = null;
      }
    }
    if (Array.isArray(tiers)) {
      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
      const matchingTier = tiers
        .filter((tier) => {
          const minQty = Number(tier?.minQty);
          const discount = Number(tier?.discount);
          return Number.isFinite(minQty) && minQty >= 1
            && Number.isFinite(discount) && discount > 0
            && qty >= minQty
            && (!tier.startsAt || tier.startsAt <= today)
            && (!tier.endsAt || tier.endsAt >= today);
        })
        .sort((a, b) => Number(b.minQty) - Number(a.minQty))[0];
      return matchingTier ? Number(matchingTier.discount) / 100 : 0;
    }

    // Retain the legacy fallback until every card has a tier metafield.
    if (qty >= 20) return 0.40;
    if (qty >= 16) return 0.38;
    if (qty >= 12) return 0.35;
    if (qty >= 8) return 0.30;
    if (qty >= 5) return 0.20;
    if (qty >= 2) return 0.10;
    return 0;
  }

  parseMoneyTextToCents(text) {
    const numeric = Number(String(text || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric * 100) : null;
  }

  getDisplayedCardUnitPriceCents(card) {
    if (this.getSelectedCardQty(card) !== 1) return null;

    const priceContainer = card.querySelector('.card-information [id*="price-"], [id*="price-"]');
    if (!priceContainer) return null;

    const priceEl =
      priceContainer.querySelector('.price__member-display .price-item--sale.price-item--last') ||
      priceContainer.querySelector('.price__sale .price-item--sale.price-item--last') ||
      priceContainer.querySelector('.price-item--sale.price-item--last') ||
      priceContainer.querySelector('.price__regular .price-item--regular') ||
      priceContainer.querySelector('.price-item--regular');

    const displayedPriceCents = this.parseMoneyTextToCents(priceEl?.textContent);
    if (!displayedPriceCents) return null;

    return displayedPriceCents;
  }

  getQtyLabelConfig(productHandle) {
    if (productHandle === '1020-universal-bottom-tray') {
      return { noun: 'tray', unit: 'tray', max: 30, divisor: 1, showUnitPrice: true };
    }

    if (productHandle === 'epic-16-cell-seed-starting-trays') {
      return { noun: 'pack', unit: 'tray', max: 24, divisor: 4, packSize: 4 };
    }

    if (
      productHandle === 'epic-tray-6-cell-garden-propagation-trays' ||
      productHandle === 'epic-tray-4-cell-garden-propagation-trays'
    ) {
      return {
        noun: 'pack', unit: 'tray', max: 24,
        divisor: productHandle === 'epic-tray-6-cell-garden-propagation-trays' ? 6 : 4,
        packSize: productHandle === 'epic-tray-6-cell-garden-propagation-trays' ? 6 : 4,
      };
    }

    if (productHandle === '5-cube-pot') {
      return { noun: 'Pot', unit: 'Pot', max: 24, divisor: 1 };
    }

    if (productHandle === '3-cube-pot') {
      return { noun: 'Pot', unit: 'Pot', max: 20, divisor: 1 };
    }

    return null;
  }

  getCardQtyUnitPriceCents(card, variant, qty, basePriceOverrideCents = null) {
    const productHandle = String(card?.querySelector('product-form')?.dataset?.productHandle || '').trim();
    const config = this.getQtyLabelConfig(productHandle);
    if (!config) return null;

    let basePriceCents = Number(basePriceOverrideCents) || Number(variant?.price) || 0;
    if (!basePriceCents) return null;

    const discountPct = this.getBulkDiscountPct(card, qty);
    const lineSubtotalCents = basePriceCents * qty;
    const totalCents = discountPct > 0
      ? lineSubtotalCents - Math.round(lineSubtotalCents * discountPct)
      : lineSubtotalCents;

    return Math.round(totalCents / (qty * config.divisor));
  }

  updateQtyDropdownLabels(card, variant, options = {}) {
    const select = card.querySelector('.eg-card-qty-select');
    if (!select || !variant) return;

    const productHandle = String(card?.querySelector('product-form')?.dataset?.productHandle || '').trim();
    const config = this.getQtyLabelConfig(productHandle);
    if (!config) return;

    const displayedBasePriceCents = options.preferDisplayedPrice ? this.getDisplayedCardUnitPriceCents(card) : null;
    if (options.preferDisplayedPrice && displayedBasePriceCents) {
      select.dataset.egDisplayBasePriceCents = String(displayedBasePriceCents);
    } else if (!options.preferDisplayedPrice) {
      delete select.dataset.egDisplayBasePriceCents;
    }
    const basePriceOverrideCents = options.preferDisplayedPrice
      ? displayedBasePriceCents || Number(select.dataset.egDisplayBasePriceCents) || null
      : null;

    Array.from(select.options).forEach((option) => {
      const qty = parseInt(option.value, 10);
      if (!qty || qty < 1) return;

      const unitPriceCents = this.getCardQtyUnitPriceCents(card, variant, qty, basePriceOverrideCents);
      if (unitPriceCents === null) return;

      const plural = qty === 1 ? '' : 's';
      const packLabel = `${qty} ${config.noun}${plural}`;
      const totalUnits = config.packSize ? qty * config.packSize : qty;
      const simpleLabel = config.packSize
        ? `${totalUnits} trays (${packLabel.toLowerCase()})`
        : packLabel;
      const verboseLabel = config.packSize
        ? `${totalUnits} trays (${packLabel.toLowerCase()}, ${this.formatMoney(unitPriceCents)}/${config.unit})`
        : config.showUnitPrice
          ? `${packLabel} (${this.formatMoney(unitPriceCents)}/${config.unit})`
          : packLabel;

      option.setAttribute('data-simple-label', simpleLabel);
      option.setAttribute('data-verbose-label', verboseLabel);
      option.textContent = option.selected && select.dataset.expanded !== 'true' ? simpleLabel : verboseLabel;
    });

    if (window.EgUpdateDropdown) window.EgUpdateDropdown(select);
  }

  refreshAllQtyDropdownLabelsFromDisplayedPrices() {
    document.querySelectorAll('.card-wrapper, .recommendations-slide').forEach((card) => {
      const variantEl = card.querySelector('[data-product-id]');
      const productId = variantEl?.dataset?.productId;
      if (!productId || !card.querySelector('.eg-card-qty-select')) return;

      const variant = this.getCurrentVariantForCard(card, productId);
      if (!variant) return;

      this.updateQtyDropdownLabels(card, variant, { preferDisplayedPrice: true });
    });
  }

  getComputedCardPricing(card, variant, showMemberPrice) {
    const qty = this.getSelectedCardQty(card);
    const hasPackQtySelector = !!card.querySelector('.eg-card-qty-select');
    const virtualBundleContext = this.getVirtualBundleMemberPricingContext(card, variant);
    const bulkDiscountPct = this.getBulkDiscountPct(card, qty);
    const memberDiscountPct = showMemberPrice ? 0.10 : 0;
    const totalDiscountPct = Math.min(1, bulkDiscountPct + memberDiscountPct);

    const basePriceCents = Number(variant?.price) || 0;
    const compareAtCents = Number(variant?.compare_at_price) || 0;
    let baselineCompareCents = compareAtCents > basePriceCents ? compareAtCents : basePriceCents;
    const memberOverridePrice = showMemberPrice
      ? this.getMemberOverridePrice(
          card,
          virtualBundleContext?.singleVariant || variant,
          qty * (virtualBundleContext?.bundleQuantity || 1)
        )
      : null;
    // Virtual bundle prices already represent the standard member bundle total.
    // When an override is active, compare it against that price, not the MSRP.
    if (showMemberPrice && virtualBundleContext && memberOverridePrice !== null) {
      baselineCompareCents = basePriceCents;
    }
    const lineQuantity = hasPackQtySelector ? qty : 1;
    const lineSubtotalCents = basePriceCents * lineQuantity;
    const effectivePriceCentsFromDiscount = bulkDiscountPct > 0
      ? Math.max(0, lineSubtotalCents - Math.round(lineSubtotalCents * totalDiscountPct))
      : Math.max(0, basePriceCents - Math.floor(basePriceCents * memberDiscountPct)) * lineQuantity;
    let effectivePriceCents = memberOverridePrice === null
      ? effectivePriceCentsFromDiscount
      : memberOverridePrice * (hasPackQtySelector ? qty : 1);
    if (virtualBundleContext && memberOverridePrice !== null) {
      effectivePriceCents = memberOverridePrice * virtualBundleContext.bundleQuantity;
    }
    const baselineDisplayCents = hasPackQtySelector ? baselineCompareCents * qty : baselineCompareCents;

    return {
      effectivePriceCents,
      baselineCompareCents: baselineDisplayCents,
      hasDiscount: baselineDisplayCents > effectivePriceCents,
    };
  }

  updatePrice(card, variant, productId) {
    const priceContainer = card.querySelector(`[id*="price-"][id*="${productId}"]`);
    if (!priceContainer) return;
    const variantId = variant?.id || '';

    // Member pricing: check global flag set in theme.liquid and eligibility data attribute.
    // data-member-eligible is rendered by price.liquid — "false" only for gift cards /
    // products tagged exclude-member-pricing. Defaults to true (opt-out model).
    const isMember = window.EPIC_GARDENING?.isGreenhouseMember === true;
    const isEligible = priceContainer.dataset.memberEligible !== 'false';
    const showMemberPrice = isMember && isEligible;

    let priceHTML = '';

    const pricing = this.getComputedCardPricing(card, variant, showMemberPrice);

    if (showMemberPrice) {
      const struckPrice = pricing.baselineCompareCents;

      priceContainer.classList.add('price--member');
      priceHTML = `
        <div class="price__container">
          <div class="price__member-display">
            <span class="visually-hidden visually-hidden--inline">Sale price</span>
            <span class="price-item price-item--sale price-item--last" data-product-id="${productId}" data-variant-id="${variantId}">
              ${this.formatMoney(pricing.effectivePriceCents)}
            </span>
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <s class="price-item price-item--regular" data-product-id="${productId}" data-variant-id="${variantId}">
              ${this.formatMoney(struckPrice)}
            </s>
          </div>
        </div>
      `;
    } else {
      priceContainer.classList.remove('price--member');

      const hasDiscount = pricing.hasDiscount;

      if (hasDiscount) {
        priceHTML = `
          <div class="price__container">
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Sale price</span>
              <span class="price-item price-item--sale price-item--last" data-product-id="${productId}" data-variant-id="${variantId}">
                ${this.formatMoney(pricing.effectivePriceCents)}
              </span>
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span>
                <s class="price-item price-item--regular" data-product-id="${productId}" data-variant-id="${variantId}">
                  ${this.formatMoney(pricing.baselineCompareCents)}
                </s>
              </span>
            </div>
          </div>
        `;
      } else {
        priceHTML = `
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular" data-product-id="${productId}" data-variant-id="${variantId}">
                ${this.formatMoney(pricing.effectivePriceCents)}
              </span>
            </div>
          </div>
        `;
      }
    }

    priceContainer.innerHTML = priceHTML;
    if (typeof window.igData?.resetDom === 'function') window.igData.resetDom();
    this.updateMemberSavingsNudge(card, variant, productId);
  }

  updateMemberSavingsNudge(card, variant, productId) {
    const nudgeEl = card.querySelector('[data-card-member-savings]');
    if (!nudgeEl || !variant) return;

    const priceContainer = card.querySelector(`[id*="price-"][id*="${productId}"]`);
    const isEligible = priceContainer ? priceContainer.dataset.memberEligible !== 'false' : true;
    const memberState = nudgeEl.dataset.cardMemberState || '';
    const isMember = memberState === 'member' || window.EPIC_GARDENING?.isGreenhouseMember === true;
    if (!isEligible) {
      nudgeEl.style.display = 'none';
      return;
    }

    if (isMember) {
      nudgeEl.style.display = 'none';
      return;
    }

    const nonMemberPricing = this.getComputedCardPricing(card, variant, false);
    const memberPricing = this.getComputedCardPricing(card, variant, true);
    const amountEl = nudgeEl.querySelector('.member-savings-amount');
    if (amountEl) {
      amountEl.textContent = this.formatMoney(memberPricing.effectivePriceCents);
    }

    const memberLink = nudgeEl.querySelector('.member-savings__link');
    const productHandle = String(card.querySelector('product-form')?.dataset?.productHandle || '').trim();
    if (memberLink && productHandle && variant?.id) {
      memberLink.href = `/products/${productHandle}?variant=${variant.id}&member-price=membership`;
    }

    nudgeEl.style.display = memberPricing.effectivePriceCents > 0 ? '' : 'none';
  }

  updateDiscountBadge(card, variant, productId) {
    const discountBadge = card.querySelector(`[id*="discount-badge-"][id*="${productId}"]`);
    if (!discountBadge) return;

    const isMember = window.EPIC_GARDENING?.isGreenhouseMember === true;
    const priceContainer = card.querySelector(`[id*="price-"][id*="${productId}"]`);
    const isEligible = priceContainer?.dataset?.memberEligible !== 'false';
    const showMemberPrice = isMember && isEligible;
    const pricing = this.getComputedCardPricing(card, variant, showMemberPrice);
    const hasDiscount = pricing.hasDiscount;
    
    if (hasDiscount) {
      const discountPercentage = Math.round(
        ((pricing.baselineCompareCents - pricing.effectivePriceCents) * 100) / pricing.baselineCompareCents
      );
      discountBadge.textContent = `${discountPercentage}% off`;
      discountBadge.style.display = '';
    } else {
      discountBadge.style.display = 'none';
    }
  }

  updateImage(card, variant, productId) {
    const image = this.getVariantImage(variant);
    if (!image?.src) return;

    const imgElement = card.querySelector(`[id*="product-image-"][id*="${productId}"] img`);
    if (imgElement) {
      // Build a responsive srcset to ensure browsers swap the image (they prefer srcset over src)
      const candidateWidths = [165, 360, 533, 720, 940, 1066];
      const maxWidth = Number(image.width) || null;
      const widths = maxWidth ? candidateWidths.filter((w) => w <= maxWidth) : candidateWidths;

      const srcset = widths
        .map((w) => `${this.withImageWidth(image.src, w)} ${w}w`)
        .join(', ');

      const src = this.withImageWidth(image.src, 533);

      imgElement.srcset = srcset;
      imgElement.setAttribute('srcset', srcset);
      imgElement.src = src;
      imgElement.setAttribute('src', src);
      imgElement.alt = image.alt || variant.name;
    }
  }

  getVariantImage(variant) {
    // Liquid `product.variants | json` can expose different shapes depending on theme/runtime.
    // Support a few common ones.
    if (variant?.featured_image) {
      if (typeof variant.featured_image === 'string') {
        return { src: variant.featured_image, alt: variant.name };
      }
      return {
        src: variant.featured_image.src || variant.featured_image.url,
        alt: variant.featured_image.alt,
        width: variant.featured_image.width,
      };
    }

    const preview = variant?.featured_media?.preview_image;
    if (preview) {
      return {
        src: preview.src || preview.url,
        alt: preview.alt,
        width: preview.width,
      };
    }

    if (variant?.image) {
      if (typeof variant.image === 'string') {
        return { src: variant.image, alt: variant.name };
      }
      return {
        src: variant.image.src || variant.image.url,
        alt: variant.image.alt,
        width: variant.image.width,
      };
    }

    return null;
  }

  withImageWidth(src, width) {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (e) {
      // Fallback (handles weird URL shapes)
      const [base, hash] = String(src).split('#');
      const baseUrl = base.replace(/([?&])width=\d+(&)?/g, (match, sep, amp) => (sep === '?' ? '?' : amp ? '&' : ''));
      const joiner = baseUrl.includes('?') ? '&' : '?';
      const next = `${baseUrl}${joiner}width=${width}`;
      return hash ? `${next}#${hash}` : next;
    }
  }

  updateButton(card, variant) {
    const submitButton = card.querySelector('.quick-add__submit');
    if (!submitButton) return;
    if (submitButton.matches('.button--early-access, [data-early-access-end]')) return;
    const form = submitButton.closest('form[data-type="add-to-cart-form"]');
    if (form?.classList.contains('x-pd-atc-form')) return;

    const preorderTimeline = card.querySelector('[data-custom-preorder-timeline], .card__custom-preorder-timeline');
    const preorderState = this.getVariantPreorderState(card, variant);
    if (preorderTimeline) {
      preorderTimeline.hidden = !preorderState.active;
      if (preorderState.timeline) preorderTimeline.innerHTML = preorderState.timeline;
    }

    const buttonText = submitButton.querySelector('span:first-child');
    if (!buttonText) return;
    const variantInput = card.querySelector('.product-variant-id') || card.closest('.recommendations-slide')?.querySelector('.product-variant-id');

    if (variant.available) {
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-disabled');
      if (variantInput) variantInput.disabled = false;
      
      // Preserve product-level custom preorder state as variants change.
      const isPreorder = this.getVariantPreorderState(card, variant).active;
      
      if (isPreorder) {
        buttonText.textContent = window.theme?.strings?.preorder || 'Pre-Order';
      } else {
        buttonText.textContent = window.theme?.strings?.addToCart || 'Add to cart';
      }
    } else {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      if (variantInput) variantInput.disabled = true;
      buttonText.textContent = window.theme?.strings?.soldOut || 'Sold out';
    }
  }

  updateAvailableOptions(card, selectedVariant) {
    // This function would disable options that aren't available
    // based on the currently selected variant
    // Current behavior: update based on availability only.
    
    const variantsScript = card.querySelector('script[id*="variants-"]');
    if (!variantsScript) return;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      return;
    }

    // Update radio buttons
    const radioButtons = card.querySelectorAll('.card-variant-radio');
    radioButtons.forEach(radio => {
      const optionPosition = parseInt(radio.dataset.optionPosition);
      const optionValue = radio.value;
      
      // Check if this option is available in any variant
      const isAvailable = variants.some(variant => {
        return variant[`option${optionPosition}`] === optionValue && variant.available;
      });
      
      radio.disabled = !isAvailable;
      
      const swatchLabel = card.querySelector(`label[for="${radio.id}"]`) || radio.closest('label');
      if (swatchLabel) {
        const pill = swatchLabel.querySelector('.color-pill');
        const targetEl = pill || swatchLabel;
        targetEl.classList.toggle('disabled', !isAvailable);
      }
    });

    // Update select options
    const selects = card.querySelectorAll('.card-variant-select');
    selects.forEach(select => {
      const optionPosition = parseInt(select.dataset.optionPosition);
      
      Array.from(select.options).forEach(option => {
        const optionValue = option.value;
        
        // Check if this option is available in any variant
        const isAvailable = variants.some(variant => {
          return variant[`option${optionPosition}`] === optionValue && variant.available;
        });
        
        option.disabled = !isAvailable;
      });
    });
  }

  formatMoney(cents) {
    let formattedMoney;

    // Check if Shopify's formatMoney function is available (most themes have this)
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      formattedMoney = Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    } else if (window.theme && window.theme.moneyFormat) {
      // Check if theme has a money format setting
      const dollars = (cents / 100).toFixed(2);
      formattedMoney = window.theme.moneyFormat
        .replace('{{amount}}', dollars)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100));
    } else {
      // Fallback to basic USD formatting
      const dollars = cents / 100;
      formattedMoney = `$${dollars.toFixed(2)}`;
    }

    return Number(cents) % 100 === 0
      ? formattedMoney.replace(/([.,])00(?=(?:\s|$))/, '')
      : formattedMoney;
  }

  getSizedImageUrl(url, size) {
    if (!url) return '';
    
    // Remove any existing size parameter
    const urlWithoutSize = url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)\./g, '.');
    
    // Add new size parameter
    const extension = urlWithoutSize.split('.').pop();
    return urlWithoutSize.replace(`.${extension}`, `_${size}.${extension}`);
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  window.cardVariantPicker = new CardVariantPicker();
}

// Also initialize on Shopify section render (for dynamic sections)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (window.cardVariantPicker) {
      window.cardVariantPicker.attachEventListeners();
    }
  });
}
